import api from './axiosConfig';
import { AttendanceRecord } from '../types';

type PenaltyUiValue = 'none' | 'warning' | 'half-day' | 'full-day';

type PunchCoordinates = {
  latitude?: number;
  longitude?: number;
};

const getBrowserCoordinates = async (): Promise<PunchCoordinates> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || !navigator.geolocation) {
    return {};
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: Number(position.coords.latitude.toFixed(8)),
          longitude: Number(position.coords.longitude.toFixed(8)),
        });
      },
      () => resolve({}),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  });
};

const toPenaltyApiValue = (value: string | number): number => {
  if (typeof value === 'number') {
    return value;
  }

  switch (value) {
    case 'none':
    case 'None':
      return 0;
    case 'warning':
    case 'Warning':
      return 1;
    case 'half-day':
    case 'HalfDay':
      return 2;
    case 'full-day':
    case 'FullDay':
      return 3;
    default:
      return 0;
  }
};

const toPenaltyUiValue = (value: string | number | null | undefined): PenaltyUiValue => {
  if (typeof value === 'number') {
    switch (value) {
      case 1:
        return 'warning';
      case 2:
        return 'half-day';
      case 3:
        return 'full-day';
      default:
        return 'none';
    }
  }

  switch (value) {
    case 'Warning':
    case 'warning':
      return 'warning';
    case 'HalfDay':
    case 'half-day':
      return 'half-day';
    case 'FullDay':
    case 'full-day':
      return 'full-day';
    default:
      return 'none';
  }
};

export const attendanceApi = {
    // Get present/absent employees for a date
    getStatusList: async (date: string, status: 'present' | 'absent') => {
      const response = await api.get('/attendance/status-list', {
        params: { date, status },
      });
      return response.data;
    },
  checkIn: async (
    employeeId: string,
    remarks?: string,
    modeOfEntry?: string,
    coordinates?: PunchCoordinates,
    selfieImageBase64?: string
  ): Promise<{ id: string }> => {
    const browserCoordinates = coordinates ?? await getBrowserCoordinates();
    const response = await api.post<{ id: string }>('/attendance/checkin', {
      employeeId,
      checkInTime: new Date().toISOString(),
      remarks,
      modeOfEntry: modeOfEntry || 'Web Based Entry',
      latitude: browserCoordinates.latitude,
      longitude: browserCoordinates.longitude,
      selfieImageBase64,
    });
    return response.data;
  },

  checkOut: async (
    employeeId: string,
    remarks?: string,
    modeOfEntry?: string,
    coordinates?: PunchCoordinates,
    selfieImageBase64?: string
  ): Promise<void> => {
    const browserCoordinates = coordinates ?? await getBrowserCoordinates();
    await api.post('/attendance/checkout', {
      employeeId,
      checkOutTime: new Date().toISOString(),
      remarks,
      modeOfEntry: modeOfEntry || 'Web Based Entry',
      latitude: browserCoordinates.latitude,
      longitude: browserCoordinates.longitude,
      selfieImageBase64,
    });
  },

  getTodayStatus: async (employeeId?: string): Promise<{ isCheckedIn: boolean; todayPunches: any[]; isWebSelfieCheckinRequired?: boolean; isWebSelfieCheckoutRequired?: boolean; allowMultipleCheckin?: boolean }> => {
    const response = await api.get('/attendance/today-status', {
      params: employeeId ? { employeeId } : {},
    });
    return response.data;
  },

  getByEmployee: async (
    employeeId: string,
    startDate: string,
    endDate: string
  ): Promise<AttendanceRecord[]> => {
    const response = await api.get<AttendanceRecord[]>(`/attendance/employee/${employeeId}`, {
      params: { startDate, endDate },
    });
    return response.data;
  },

  manualEntry: async (data: {
    employeeId: string;
    date: string;
    checkInTime?: string;
    checkOutTime?: string;
    remarks?: string;
  }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/attendance/manual', data);
    return response.data;
  },

  // Get all attendance logs with filters
  getAttendanceRecords: async (filters: {
    employeeId?: string;
    startDate?: string;
    endDate?: string;
    type?: string;
    status?: string;
    modeOfEntry?: string;
    locationName?: string;
    entityType?: string;
    departmentId?: string;
    designationId?: string;
    branchId?: string;
    entityName?: string;
    recordedBy?: string;
    remarks?: string;
    groupBy?: string;
    sortDirection?: 'asc' | 'desc';
    pageNumber?: number;
    pageSize?: number;
  }): Promise<{ data: any[]; total: number; pageNumber: number; pageSize: number }> => {
    const response = await api.get('/attendance/logs', { params: filters });
    return response.data;
  },

  // Export attendance logs
  exportRecords: async (filters: {
    employeeId?: string;
    startDate?: string;
    endDate?: string;
    type?: string;
    status?: string;
    modeOfEntry?: string;
    locationName?: string;
    entityType?: string;
    departmentId?: string;
    designationId?: string;
    branchId?: string;
    entityName?: string;
    recordedBy?: string;
    remarks?: string;
    tableSearch?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<Blob> => {
    const response = await api.get('/attendance/logs/export', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },

  // Regularization requests
  getRegularizationRequests: async (filters?: {
    status?: string;
    requestType?: 'Attendance' | 'Overtime' | string;
    employeeId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<any[]> => {
    const response = await api.get('/attendance/regularization', { params: filters });
    const statusMap: Record<number, string> = { 0: 'Pending', 1: 'Approved', 2: 'Rejected' };
    return response.data.map((item: any) => ({
      ...item,
      status: item.statusName || (typeof item.status === 'number' ? statusMap[item.status] ?? 'Pending' : item.status),
      requestType: item.requestTypeName || item.requestType || 'Attendance',
      requestedOtHours: item.requestedOtHours ?? item.RequestedOtHours ?? null,
      approvedByName: item.approvedByName ?? item.ApprovedByName ?? undefined,
      approverRemarks: item.approverRemarks ?? item.ApproverRemarks ?? undefined,
    }));
  },

  submitRegularizationRequest: async (data: {
    employeeId: string;
    attendanceId?: string;
    date: string;
    requestType?: 'Attendance' | 'Overtime';
    requestedCheckIn?: string | null;
    requestedCheckOut?: string | null;
    requestedOtHours?: number | null;
    reason: string;
  }): Promise<{ id: string }> => {
    const response = await api.post('/attendance/regularization', data);
    return response.data;
  },

  approveRegularization: async (id: string): Promise<void> => {
    await api.post(`/attendance/regularization/${id}/approve`);
  },

  rejectRegularization: async (id: string, reason: string): Promise<void> => {
    await api.post(`/attendance/regularization/${id}/reject`, { reason });
  },

  // Manual OT/HOT
  submitManualOT: async (data: {
    employeeId: string;
    date: string;
    otHours: number;
    hotHours: number;
    remarks?: string;
  }): Promise<{ id: string }> => {
    const response = await api.post('/attendance/manual-ot', data);
    return response.data;
  },
  getManualOTHistory: async (): Promise<any[]> => {
    const response = await api.get('/attendance/manual-ot');
    return response.data;
  },
  // Attendance report
  getAttendanceReport: async (filters: {
    fromDate?: string;
    toDate?: string;
    department?: string;
    employeeId?: string;
    status?: string;
  }): Promise<{ summary: any; data: any[] }> => {
    const response = await api.get('/attendance/report', { params: filters });
    return response.data;
  },

  // Employee self attendance report (dedicated endpoint)
  getMyAttendanceReport: async (filters: {
    date?: string;
    fromDate?: string;
    toDate?: string;
    status?: string;
  }): Promise<{ summary: any; data: any[] }> => {
    const response = await api.get('/my-attendance/report', { params: filters });
    return response.data;
  },

  // Export attendance report
  exportReport: async (filters: {
    fromDate?: string;
    toDate?: string;
    department?: string;
    employeeId?: string;
    status?: string;
    format: 'excel' | 'csv' | 'pdf';
  }): Promise<Blob> => {
    const response = await api.get('/attendance/report/export', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },

  // Auto attendance process
  runAutoProcess: async (date?: string): Promise<{ processed: number }> => {
    const response = await api.post('/attendance/auto-process', { date });
    return response.data;
  },

  // Attendance Rules
  getAttendanceRules: async (shiftId?: string): Promise<any> => {
    const response = await api.get('/attendance/rules', {
      params: shiftId ? { shiftId } : undefined,
    });
    const data = response.data ?? {};

    return {
      id: data.id,
      shiftId: data.shiftId,
      minWorkingHours: data.minimumWorkingHours ?? data.minWorkingHours ?? 8,
      lateArrivalGraceMinutes: data.lateArrivalGraceMinutes ?? 15,
      lateArrivalPenalty: toPenaltyUiValue(data.lateArrivalPenalty),
      earlyCheckoutGraceMinutes: data.earlyCheckoutGraceMinutes ?? 10,
      earlyCheckoutPenalty: toPenaltyUiValue(data.earlyCheckoutPenalty),
      overtimeEnabled: data.overtimeEnabled ?? true,
      minimumOvertimeMinutes: data.minimumOvertimeMinutes ?? 30,
      maxOvertimeHours: data.maxOvertimeHoursPerDay ?? data.maxOvertimeHours ?? 4,
      regularizationRequestLimit: data.regularizationRequestLimit ?? 0,
      regularizationLimitPeriod: data.regularizationLimitPeriod ?? 0,
    };
  },

  saveAttendanceRules: async (data: {
    id?: string;
    shiftId?: string;
    minWorkingHours: number;
    lateArrivalGraceMinutes: number;
    lateArrivalPenalty: string | number;
    earlyCheckoutGraceMinutes: number;
    earlyCheckoutPenalty: string | number;
    overtimeEnabled: boolean;
    minimumOvertimeMinutes: number;
    maxOvertimeHours: number;
    regularizationRequestLimit: number;
    regularizationLimitPeriod: number;
  }): Promise<void> => {
    await api.put('/attendance/rules', {
      id: data.id || undefined,
      shiftId: data.shiftId || null,
      minimumWorkingHours: data.minWorkingHours,
      lateArrivalGraceMinutes: data.lateArrivalGraceMinutes,
      lateArrivalPenalty: toPenaltyApiValue(data.lateArrivalPenalty),
      earlyCheckoutGraceMinutes: data.earlyCheckoutGraceMinutes,
      earlyCheckoutPenalty: toPenaltyApiValue(data.earlyCheckoutPenalty),
      overtimeEnabled: data.overtimeEnabled,
      minimumOvertimeMinutes: data.minimumOvertimeMinutes,
      maxOvertimeHoursPerDay: data.maxOvertimeHours,
      regularizationRequestLimit: data.regularizationRequestLimit,
      regularizationLimitPeriod: data.regularizationLimitPeriod,
    });
  },

  // Advance Settings
  getAdvanceSettings: async (): Promise<any> => {
    const response = await api.get('/attendance/advance-settings');
    return response.data;
  },

  saveAdvanceSettings: async (data: {
    halfDayEnabled: boolean;
    halfDayHours: number;
    accrualEnabled: boolean;
    accrualFrequency: string;
    accrualRate: number;
    carryForwardEnabled: boolean;
    maxCarryForwardDays: number;
    carryForwardExpiryDays: number;
    negativeLeavesEnabled: boolean;
  }): Promise<void> => {
    await api.put('/attendance/advance-settings', data);
  },

  // Import biometric logs
  importBiometricLogs: async (file: File): Promise<{ imported: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ imported: number }>('/attendance/import-biometric', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Auto process schedule
  getAutoProcessSchedule: async (): Promise<{ runTime: string; enabled: boolean }> => {
    const response = await api.get('/attendance/auto-process/schedule');
    return response.data;
  },

  saveAutoProcessSchedule: async (data: { runTime: string; enabled: boolean }): Promise<void> => {
    await api.put('/attendance/auto-process/schedule', data);
  },

  // Document-aligned auto attendance settings
  getAutoAttendanceSettings: async (): Promise<{
    enableAutoAttendance: boolean;
    processTime: string;
    lateMarkAfterMinutes: number;
    halfDayAfterHours: number;
    absentAfterHours: number;
  }> => {
    const response = await api.get('/auto-attendance');
    return response.data;
  },

  saveAutoAttendanceSettings: async (data: {
    enableAutoAttendance: boolean;
    processTime: string;
    lateMarkAfterMinutes: number;
    halfDayAfterHours: number;
    absentAfterHours: number;
  }): Promise<void> => {
    await api.put('/auto-attendance', data);
  },

  // Face recognition based check-in
  checkInWithFace: async (
    employeeId: string,
    remarks?: string,
    faceImageBase64?: string
  ): Promise<{ id: string }> => {
    const browserCoordinates = await getBrowserCoordinates();
    const response = await api.post<{ id: string }>('/attendance/checkin', {
      employeeId,
      checkInTime: new Date().toISOString(),
      remarks: remarks || 'Face Recognition Punch',
      modeOfEntry: 'Face Recognition',
      latitude: browserCoordinates.latitude,
      longitude: browserCoordinates.longitude,
      faceImageBase64,
    });
    return response.data;
  },

  // Face recognition based check-out
  checkOutWithFace: async (
    employeeId: string,
    remarks?: string,
    faceImageBase64?: string
  ): Promise<void> => {
    const browserCoordinates = await getBrowserCoordinates();
    await api.post('/attendance/checkout', {
      employeeId,
      checkOutTime: new Date().toISOString(),
      remarks: remarks || 'Face Recognition Punch',
      modeOfEntry: 'Face Recognition',
      latitude: browserCoordinates.latitude,
      longitude: browserCoordinates.longitude,
      faceImageBase64,
    });
  },

  // Anonymous kiosk check-in (for shared devices)
  checkInAnonymous: async (
    employeeId: string,
    remarks?: string,
    coordinates?: PunchCoordinates,
    faceImageBase64?: string
  ): Promise<{ id: string }> => {
    const browserCoordinates = coordinates ?? await getBrowserCoordinates();
    const response = await api.post<{ id: string }>('/attendance/checkin', {
      employeeId,
      checkInTime: new Date().toISOString(),
      remarks: remarks || 'Kiosk Entry',
      modeOfEntry: faceImageBase64 ? 'Face Recognition' : 'Kiosk',
      latitude: browserCoordinates.latitude,
      longitude: browserCoordinates.longitude,
      faceImageBase64,
    });
    return response.data;
  },

  // Anonymous kiosk check-out (for shared devices)
  checkOutAnonymous: async (
    employeeId: string,
    remarks?: string,
    coordinates?: PunchCoordinates,
    faceImageBase64?: string
  ): Promise<void> => {
    const browserCoordinates = coordinates ?? await getBrowserCoordinates();
    await api.post('/attendance/checkout', {
      employeeId,
      checkOutTime: new Date().toISOString(),
      remarks: remarks || 'Kiosk Entry',
      modeOfEntry: faceImageBase64 ? 'Face Recognition' : 'Kiosk',
      latitude: browserCoordinates.latitude,
      longitude: browserCoordinates.longitude,
      faceImageBase64,
    });
  },
};
